import { SIGNUP_TERMS_AND_CONDITIONS_DEFAULTS } from 'constants/uiConfig'
import { decryptCredentials, getCookie } from './storageUtils'
import { countryCurrency } from 'components/SignupPage/RightSection/SignupForm/constant'

/**
 * Get affiliate code from the provided code or decrypted cookie
 *
 * @param {string} affiliateCode - User's affiliate code
 * @returns {string|undefined} - Returns affiliate code if available, undefined otherwise
 */
export const getAffiliateCodeForPayload = (affiliateCode) => {
  const refToken = decryptCredentials(getCookie('refToken'))

  if (refToken) {
    return refToken
  }

  return undefined
}

/**
 * List of country codes that are selectable for the current tenant/domain.
 * `allCountries` is already filtered by allowedCountryMapping/blockCountryMapping
 * in the getAllCountries saga, so its rows are the source of truth for "allowed".
 *
 * @param {{ rows?: Array<{ code: string }> }} allCountries - Filtered countries from store
 * @returns {string[]} - Uppercase ISO codes that are allowed
 */
export const getAllowedCountryCodes = (allCountries) =>
  allCountries?.rows?.map(({ code }) => code) || []

/**
 * Resolve a geo-detected country code to a safe initial value for the signup form.
 * If the detected country is not in the allowed (filtered) list, returns ''
 * so the form does not pre-submit a restricted country (e.g. NL on wunderwins).
 *
 * @param {string} countryCode - Geo-detected country code (userIPCountry)
 * @param {object} allCountries - Filtered countries from store
 * @returns {string} - Uppercase code if allowed, otherwise ''
 */
export const getAllowedInitialCountryCode = (countryCode, allCountries) => {
  const code = countryCode?.toUpperCase() || ''
  return getAllowedCountryCodes(allCountries).includes(code) ? code : ''
}

/**
 * Resolve the default checked state of the signup consent checkboxes for the current tenant.
 * Tenant config (`signupTermsAndConditions` in public/config/[tenant].json) overrides
 * SIGNUP_TERMS_AND_CONDITIONS_DEFAULTS key by key, so a tenant only declares what differs.
 *
 * @param {object} signupTermsAndConditions - `general.signupTermsAndConditions` from the ui-config store
 * @returns {{ privacyPolicy: boolean, termsAndConditions: boolean, newsLetter: boolean, sms: boolean, newsletterAndSms: boolean }}
 */
export const getSignupTermsAndConditions = (signupTermsAndConditions) =>
  Object.keys(SIGNUP_TERMS_AND_CONDITIONS_DEFAULTS).reduce((conditions, key) => {
    const value = signupTermsAndConditions?.[key]
    conditions[key] = value === undefined
      ? SIGNUP_TERMS_AND_CONDITIONS_DEFAULTS[key]
      : value === true || value === 'true'
    return conditions
  }, {})

/**
 * Resolves the default currency to pre-select on the signup form.
 * Priority:
 * 1. Tenant config language-to-currency mapping (`generalConfig.defaultCurrencyByLanguage[activeLanguage]`), e.g. "PT-BR" -> "BRL".
 * 2. Country-to-currency mapping based on the user's geo-detected / selected country (`countryCurrency[allowedCountryCode]`).
 * In both cases, if the tenant details provide `matchingCurrencies`, the currency must be in that list.
 *
 * @param {Object} params
 * @param {string} [params.activeLanguage] - Currently active or selected language code (e.g. 'PT-BR')
 * @param {string} [params.allowedCountryCode] - Geo-detected / initial allowed country code (e.g. 'BR')
 * @param {Object} [params.tenantDetails] - Tenant detail object from backend containing tenantConfig.matchingCurrencies
 * @param {Object} [params.generalConfig] - Tenant UI config (`state.uiConfig.general`) containing defaultCurrencyByLanguage
 * @param {Object} [params.countryCurrencyMap] - Optional map of country code to currency code (defaults to countryCurrency)
 * @returns {string} Currency code to select, or '' if no valid match
 */
export const getDefaultSignupCurrency = ({
  activeLanguage,
  allowedCountryCode,
  tenantDetails,
  generalConfig,
  countryCurrencyMap = countryCurrency
} = {}) => {
  const matchingCurrencies = tenantDetails?.tenantConfig?.matchingCurrencies
  const langKey = activeLanguage?.toUpperCase()

  // 1. Language-based default from tenant configuration (e.g. "PT-BR": "BRL")
  const currencyByLang = generalConfig?.defaultCurrencyByLanguage?.[langKey]
  if (currencyByLang) {
    if (!matchingCurrencies || matchingCurrencies.includes(currencyByLang)) {
      return currencyByLang
    }
  }

  // 2. Country-based default (from geo-IP country)
  const currencyByCountry = countryCurrencyMap?.[allowedCountryCode]
  if (currencyByCountry) {
    if (!matchingCurrencies || matchingCurrencies.includes(currencyByCountry)) {
      return currencyByCountry
    }
  }

  return ''
}
